import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PasswordEntry } from './PasswordList';
import { toast } from '@/components/ui/use-toast';

interface AddEditModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (password: Omit<PasswordEntry, 'id' | 'createdAt' | 'updatedAt'>) => void;
  password: PasswordEntry | null;
}

const AddEditModal: React.FC<AddEditModalProps> = ({
  open,
  onClose,
  onSave,
  password
}) => {
  const [formData, setFormData] = useState<Omit<PasswordEntry, 'id' | 'createdAt' | 'updatedAt'>>({
    title: '',
    username: '',
    email: '',
    password: '',
    website: '',
    phoneNumber: '',
    category: 'personal',
    notes: '',
    customFields: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (password) {
      // Clone the password object without id and timestamps
      const { id, createdAt, updatedAt, ...rest } = password;
      setFormData(rest);
    } else {
      // Reset form for new password
      setFormData({
        title: '',
        username: '',
        email: '',
        password: '',
        website: '',
        phoneNumber: '',
        category: 'pessoal',
        notes: '',
        customFields: []
      });
    }
  }, [password, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (value: string) => {
    setFormData(prev => ({ ...prev, category: value }));
  };

  const handleCustomFieldChange = (index: number, field: 'label' | 'value', value: string) => {
    setFormData(prev => {
      const updatedFields = [...(prev.customFields || [])];
      if (!updatedFields[index]) {
        updatedFields[index] = { label: '', value: '' };
      }
      updatedFields[index][field] = value;
      return { ...prev, customFields: updatedFields };
    });
  };

  const addCustomField = () => {
    setFormData(prev => ({
      ...prev,
      customFields: [...(prev.customFields || []), { label: '', value: '' }]
    }));
  };

  const removeCustomField = (index: number) => {
    setFormData(prev => {
      const updatedFields = [...(prev.customFields || [])];
      updatedFields.splice(index, 1);
      return { ...prev, customFields: updatedFields };
    });
  };

  const validateForm = (): boolean => {
    if (!formData.title.trim()) {
      toast({
        title: "Título em falta",
        description: "Insira um título para a sua entrada de palavra-passe",
        variant: "destructive"
      });
      return false;
    }

    if (!formData.password.trim()) {
      toast({
        title: "Palavra-passe perdida",
        description: "Por favor, introduza uma palavra-passe",
        variant: "destructive"
      });
      return false;
    }

    // Validate custom fields
    const hasEmptyCustomFields = formData.customFields?.some(
      field => (field.label.trim() === '' && field.value.trim() !== '') || 
               (field.label.trim() !== '' && field.value.trim() === '')
    );

    if (hasEmptyCustomFields) {
      toast({
        title: "Campos personalizados inválidos",
        description: "Os campos personalizados devem ter o rótulo e o valor preenchidos.",
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    try {
      setIsSubmitting(true);
      
      if (!validateForm()) {
        return;
      }
      
      // Filter out empty custom fields
      const filteredCustomFields = formData.customFields?.filter(
        field => field.label.trim() !== '' && field.value.trim() !== ''
      );
      
      // Create a clean password entry
      const passwordEntry = {
        ...formData,
        customFields: filteredCustomFields,
        password: formData.password.trim()
      };
      
      // Call onSave with the password entry
      await onSave(passwordEntry);
      
      // Close the modal
      onClose();
      
      toast({
        title: "Successo",
        description: password ? "Palavra-passe atualizada com sucesso" : "Palavra-passe adicionada com sucesso"
      });
    } catch (error) {
      console.error('Erro ao guardar a palavra-passe:', error);
      toast({
        title: "Erro",
        description: "Falha ao guardar a palavra-passe. Por favor, tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={open => !open && onClose()}>
      <DialogContent 
        className="max-w-lg max-h-[85vh] overflow-y-auto" 
        aria-describedby="add-edit-dialog-description"
      >
        <DialogHeader>
          <DialogTitle>{password ? 'Editar palavra-passe' : 'Adicionar nova palavra-passe'}</DialogTitle>
          <DialogDescription id="add-edit-dialog-description">
            {password 
              ? 'Edite os detalhes da sua palavra-passe existente. Todos os campos marcados com * são obrigatórios.'
              : 'Adicione uma nova palavra-passe ao seu cofre. Todos os campos marcados com * são obrigatórios.'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Título *</Label>
            <Input
              id="title"
              name="title"
              placeholder="Gmail, Twitter, Conta bancária"
              value={formData.title}
              onChange={handleChange}
              required
              autoComplete="off"
              aria-required="true"
            />
          </div>
          
          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select
              value={formData.category}
              onValueChange={handleCategoryChange}
            >
              <SelectTrigger id="category">
                <SelectValue placeholder="Selecione a categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="social">Social</SelectItem>
                <SelectItem value="bancário">Bancário</SelectItem>
                <SelectItem value="trabalho">Trabalho</SelectItem>
                <SelectItem value="pessoal">Pessoal</SelectItem>
                <SelectItem value="outro">Outro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Username */}
          <div className="space-y-2">
            <Label htmlFor="username">Nome de usuário</Label>
            <Input
              id="username"
              name="username"
              placeholder="Nome de usuário"
              value={formData.username || ''}
              onChange={handleChange}
              autoComplete="username"
            />
          </div>
          
          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="pocket.email@exemplo.com"
              value={formData.email || ''}
              onChange={handleChange}
              autoComplete="email"
            />
          </div>
          
          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password">Palavra-passe *</Label>
            <Input
              id="password"
              name="password"
              type="password"
              placeholder="Digite a sua palavra-passe"
              value={formData.password}
              onChange={handleChange}
              required
              autoComplete="new-password"
              aria-required="true"
              className="font-lato"
            />
          </div>
          
          {/* Website */}
          <div className="space-y-2">
            <Label htmlFor="website">Página web</Label>
            <Input
              id="website"
              name="website"
              placeholder="exemplo.com"
              value={formData.website || ''}
              onChange={handleChange}
              autoComplete="url"
            />
          </div>
          
          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phoneNumber">Número de telefone</Label>
            <Input
              id="phoneNumber"
              name="phoneNumber"
              placeholder="+351 931485312"
              value={formData.phoneNumber || ''}
              onChange={handleChange}
              autoComplete="tel"
              className="font-lato"
            />
          </div>
          
          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notas</Label>
            <Textarea
              id="notes"
              name="notas"
              placeholder="Notas ou informações adicionais"
              value={formData.notes || ''}
              onChange={handleChange}
              rows={3}
              autoComplete="off"
            />
          </div>
          
          {/* Custom Fields */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Campos personalizados</Label>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={addCustomField}
              >
                <Plus size={16} className="mr-1" />
                Adicionar campo
              </Button>
            </div>
            
            {formData.customFields && formData.customFields.map((field, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Input
                  id={`custom-field-${index}-label`}
                  value={field.label}
                  onChange={(e) => handleCustomFieldChange(index, 'label', e.target.value)}
                  placeholder="Nome do campo"
                  className="font-lato"
                />
                <Input
                  id={`custom-field-${index}-value`}
                  value={field.value}
                  onChange={(e) => handleCustomFieldChange(index, 'value', e.target.value)}
                  placeholder="Valor do campo"
                  className="font-lato"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeCustomField(index)}
                >
                  <Trash2 size={18} />
                </Button>
              </div>
            ))}
          </div>
        </form>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Salvando...' : password ? 'Salvar Alterações' : 'Adicionar Palavra-passe'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditModal;
